import { useState } from "react";
import { Pencil, Plus, Trash2, User, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import {
  ProjectContact,
  useProjectContacts,
  useCreateProjectContact,
  useUpdateProjectContact,
  useDeleteProjectContact,
} from "@/hooks/useProjectContacts";
import { usePeople } from "@/hooks/usePeople";

interface ProjectContactsSectionProps {
  projectId: string;
  customerId?: string | null;
  readOnly?: boolean;
}

function EditableRow({
  contact,
  projectId,
  peopleOptions,
  customerId,
}: {
  contact: ProjectContact;
  projectId: string;
  peopleOptions: { value: string; label: string; subtitle?: string }[];
  customerId?: string | null;
}) {
  const updateContact = useUpdateProjectContact();
  const deleteContact = useDeleteProjectContact();
  const [editing, setEditing] = useState(false);
  const [editDescription, setEditDescription] = useState(contact.description);
  const [editPersonId, setEditPersonId] = useState<string | null>(
    contact.person_id,
  );

  const handleSave = () => {
    if (!editPersonId || !editDescription.trim()) return;
    updateContact.mutate(
      {
        id: contact.id,
        projectId,
        updates: {
          description: editDescription.trim(),
          person_id: editPersonId,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  const handleCancel = () => {
    setEditDescription(contact.description);
    setEditPersonId(contact.person_id);
    setEditing(false);
  };

  if (editing) {
    return (
      <TableRow>
        <TableCell>
          <Input
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            autoFocus
          />
        </TableCell>
        <TableCell>
          <EntityCombobox
            options={peopleOptions}
            value={editPersonId}
            onChange={setEditPersonId}
            placeholder="Select contact"
            searchPlaceholder="Search people..."
            emptyMessage="No people found."
            createLabel="Create new person"
            renderCreateForm={({ onSuccess, onCancel, initialName }) => (
              <InlinePersonForm
                onSuccess={onSuccess}
                onCancel={onCancel}
                initialName={initialName}
                defaultType="contact"
              />
            )}
          />
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell>
          <div className="flex gap-1">
            <Button
              offlineEdit
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary"
              onClick={handleSave}
              disabled={
                !editPersonId ||
                !editDescription.trim() ||
                updateContact.isPending
              }
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={handleCancel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{contact.description}</TableCell>
      <TableCell>
        {contact.person
          ? `${contact.person.first_name} ${contact.person.last_name}`
          : "Unknown"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {contact.person?.phone || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {contact.person?.email || "—"}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            offlineEdit
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            offlineEdit
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => deleteContact.mutate({ id: contact.id, projectId })}
            disabled={deleteContact.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ProjectContactsSection({
  projectId,
  customerId,
  readOnly = false,
}: ProjectContactsSectionProps) {
  const { data: contacts = [] } = useProjectContacts(projectId);
  const { data: allPeople } = usePeople(["contact", "freelancer"]);
  const createContact = useCreateProjectContact();

  const [isAdding, setIsAdding] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [newPersonId, setNewPersonId] = useState<string | null>(null);

  const peopleOptions =
    allPeople?.map((p) => ({
      value: p.id,
      label: `${p.first_name} ${p.last_name}`,
      subtitle:
        [
          p.person_type === "freelancer" ? "Freelancer" : undefined,
          p.companies?.name,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
    })) ?? [];

  const handleAdd = () => {
    if (!newPersonId || !newDescription.trim()) return;
    createContact.mutate(
      {
        project_id: projectId,
        person_id: newPersonId,
        description: newDescription.trim(),
      },
      {
        onSuccess: () => {
          setNewDescription("");
          setNewPersonId(null);
          setIsAdding(false);
        },
      },
    );
  };

  const handleCancel = () => {
    setNewDescription("");
    setNewPersonId(null);
    setIsAdding(false);
  };

  if (readOnly) {
    if (contacts.length === 0) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Contacts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium">
                    {contact.description}
                  </TableCell>
                  <TableCell>
                    {contact.person
                      ? `${contact.person.first_name} ${contact.person.last_name}`
                      : "Unknown"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {contact.person?.phone || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {contact.person?.email || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Contacts</CardTitle>
        {!isAdding && (
          <Button
            offlineEdit
            variant="outline"
            size="sm"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Contact
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {contacts.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground">
            No contacts added yet.
          </p>
        )}

        {contacts.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <EditableRow
                  key={contact.id}
                  contact={contact}
                  projectId={projectId}
                  peopleOptions={peopleOptions}
                  customerId={customerId}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {isAdding && (
          <div className="space-y-3 pt-3 mt-3 border-t">
            <div>
              <label className="text-sm font-medium mb-1 block">
                Description
              </label>
              <Input
                placeholder='e.g. "Time Keeper", "Security Contact"'
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Contact Person
              </label>
              <EntityCombobox
                options={peopleOptions}
                value={newPersonId}
                onChange={setNewPersonId}
                placeholder="Select contact person"
                searchPlaceholder="Search people..."
                emptyMessage="No people found."
                createLabel="Create new person"
                renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                  <InlinePersonForm
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    initialName={initialName}
                    defaultType="contact"
                  />
                )}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                offlineEdit
                size="sm"
                onClick={handleAdd}
                disabled={
                  !newPersonId ||
                  !newDescription.trim() ||
                  createContact.isPending
                }
              >
                {createContact.isPending ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
